"use client";

import { useEffect, useState } from "react";

import type { HallowABFlag } from "../types/enums/HallowABFlag.enum";
import { useMixpanel } from "./useMixpanel";

/**
 * Resolves a flag once when Mixpanel finishes loading and pins that value for
 * the life of the component, so a session never switches variants mid-flow
 * (e.g. a checkout session returning from a Stripe redirect leg must complete
 * on the same flow it started on).
 *
 * Returns `undefined` until the flag resolves so callers can distinguish
 * "not yet known" from "not targeted".
 */
export const usePinnedFlag = (flag: HallowABFlag): boolean | undefined => {
  const { flags, isLoading } = useMixpanel();
  const [pinned, setPinned] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const value = flags?.[flag];
    if (isLoading || pinned !== undefined || value === undefined) return;
    setPinned(!!value.isTargeted);
  }, [isLoading, pinned, flag, flags]);

  return pinned;
};
